import React, { useState, useEffect } from "react";
import axiosInstance from "../../api/axios";
import { useSelector } from "react-redux";
import Pagination from "../../components/Pagination";
import SortableTableHeader from "../../components/SortableTableHeader";
import { useSearchParams } from "react-router-dom";

const inputCls =
  "w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30";
const labelCls =
  "block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5";

const WalletStatementReport = () => {
  const token = useSelector((state) => state.auth.token);
  const [searchParams] = useSearchParams();

  const [filters, setFilters] = useState({
    username: "",
    status: "",
    startDate: "",
    endDate: "",
    order: "",
    page: 1,
    pageSize: 30,
    walletType: "GUSD",
  });
  const [tempFilters, setTempFilters] = useState({
    username: "",
    status: "",
    startDate: "",
    endDate: "",
    order: "",
    page: 1,
    pageSize: 30,
    walletType: "GUSD",
  });

  useEffect(() => {
    const username = searchParams.get("username") || "";
    const walletType = searchParams.get("walletType") || "GUSD";
    if (username || walletType !== "GUSD") {
      setFilters((prev) => ({ ...prev, username, walletType }));
      setTempFilters((prev) => ({ ...prev, username, walletType }));
    }
  }, [searchParams]);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRows: 0 });
  const [sort, setSort] = useState({ key: "", direction: "asc" });
  const [sortedData, setSortedData] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value && key !== "walletType") params[key] = value;
      });
      const res = await axiosInstance.get(`/api/admin/reports/statement/${filters.walletType}`, { params, headers: { token } });
      const newData = res.data.data.data || [];
      setData(newData);
      setSortedData(newData);
      setPagination({
        currentPage: Number(filters.page) || 1,
        totalPages: res.data.data.totalPages || 1,
        totalRows: res.data.data.totalRows || 0,
      });
    } catch (err) {
      setData([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [filters]);

  useEffect(() => {
    if (!data.length) return;
    const sorted = [...data].sort((a, b) => {
      if (!sort.key) return 0;
      let aValue = a[sort.key];
      let bValue = b[sort.key];
      if (aValue === null || aValue === undefined) aValue = "";
      if (bValue === null || bValue === undefined) bValue = "";
      if (sort.key === "amount" || sort.key === "balance") {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
        return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
      }
      if (sort.key === "createdAt") {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
        return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
      }
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
      return sort.direction === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });
    setSortedData(sorted);
  }, [data, sort]);

  const handleTempFilterChange = (e) => {
    const { name, value } = e.target;
    setTempFilters((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => setFilters({ ...tempFilters });

  const clearFilters = () => {
    const d = { username: "", status: "", startDate: "", endDate: "", order: "", page: 1, pageSize: 30, walletType: filters.walletType };
    setTempFilters(d);
    setFilters(d);
  };

  const handleSort = (key) => {
    setSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handlePageChange = (newPage) => setFilters((prev) => ({ ...prev, page: newPage }));

  const filterByToday = () => {
    const today = new Date().toISOString().split("T")[0];
    const u = { ...tempFilters, startDate: today, endDate: today, page: 1 };
    setTempFilters(u);
    setFilters(u);
  };

  const exportToExcel = async () => {
    try {
      const params = {};
      if (filters.username) params.username = filters.username;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      params.pageSize = 10000;
      params.page = 1;
      const res = await axiosInstance.get(`/api/admin/reports/statement/${filters.walletType}`, { params, headers: { token } });
      const exportData = res.data.data.data;
      if (!exportData.length) { alert("No data to export"); return; }
      const headers = ["Username", "Type", "Amount", "Balance", "Description", "Created At"];
      const csvContent = [
        headers.join(","),
        ...exportData.map((row) =>
          [row.username || "", row.type || "", row.amount || "", row.balance || "", row.description || "", new Date(row.createdAt).toLocaleString()].join(",")
        ),
      ].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.setAttribute("href", URL.createObjectURL(blob));
      link.setAttribute("download", `${filters.walletType}_wallet_statement_report_${new Date().toISOString().split("T")[0]}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      alert("Export failed. Please try again.");
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
        <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
        Reports
      </div>
      <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">
        {filters.walletType} Wallet <span className="text-gradient-ginox">Statement</span>
      </h1>

      <div className="mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <div>
            <label className={labelCls}>Wallet Type</label>
            <select name="walletType" value={tempFilters.walletType} onChange={handleTempFilterChange} className={inputCls}>
              <option value="GUSD">GUSD</option>
              <option value="BFM">BFM</option>
              <option value="APP_CREDIT">APP Credit</option>
              <option value="FOREX">FOREX</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Username / WalletAddress</label>
            <input name="username" value={tempFilters.username} onChange={handleTempFilterChange} placeholder="Search…" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Status</label>
            <select name="status" value={tempFilters.status} onChange={handleTempFilterChange} className={inputCls}>
              <option value="">All Types</option>
              <option value="credit">Credit</option>
              <option value="debit">Debit</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Start Date</label>
            <input type="date" name="startDate" value={tempFilters.startDate} onChange={handleTempFilterChange} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>End Date</label>
            <input type="date" name="endDate" value={tempFilters.endDate} onChange={handleTempFilterChange} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Order</label>
            <select name="order" value={tempFilters.order} onChange={handleTempFilterChange} className={inputCls}>
              <option value="">Default</option>
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Page Size</label>
            <select name="pageSize" value={tempFilters.pageSize} onChange={handleTempFilterChange} className={inputCls}>
              {[10, 15, 20, 25, 30, 50].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <button type="button" onClick={applyFilters} className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors">
            Apply Filters
          </button>
          <button type="button" onClick={clearFilters} className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors">
            Clear Filters
          </button>
          <button type="button" onClick={filterByToday} className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors">
            Today
          </button>
          <button type="button" onClick={exportToExcel} className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors">
            Export CSV
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              <SortableTableHeader sortKey="sno" currentSort={sort} onSort={handleSort} className="text-left">S.No</SortableTableHeader>
              <SortableTableHeader sortKey="username" currentSort={sort} onSort={handleSort} className="text-left">Username</SortableTableHeader>
              <SortableTableHeader sortKey="tranx_id" currentSort={sort} onSort={handleSort} className="text-left">Tranx ID</SortableTableHeader>
              <SortableTableHeader sortKey="tranx_type" currentSort={sort} onSort={handleSort} className="text-left">Tranx Type</SortableTableHeader>
              <SortableTableHeader sortKey="wallet_type" currentSort={sort} onSort={handleSort} className="text-left">Wallet Type</SortableTableHeader>
              <SortableTableHeader sortKey="amount" currentSort={sort} onSort={handleSort} className="text-left">Amount</SortableTableHeader>
              <SortableTableHeader sortKey="total" currentSort={sort} onSort={handleSort} className="text-left">Total</SortableTableHeader>
              <SortableTableHeader sortKey="message" currentSort={sort} onSort={handleSort} className="text-left">Message</SortableTableHeader>
              <SortableTableHeader sortKey="createdAt" currentSort={sort} onSort={handleSort} className="text-left">Created At</SortableTableHeader>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="py-12 text-center text-sm text-muted-foreground">Loading...</td></tr>
            ) : sortedData.length === 0 ? (
              <tr><td colSpan={9} className="py-12 text-center text-sm text-muted-foreground">No data found</td></tr>
            ) : (
              sortedData.map((item, index) => (
                <tr key={item._id} className="group hover:bg-white/[0.02] transition-colors">
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{(filters.page - 1) * filters.pageSize + index + 1}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{item.username}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{item.tranx_id}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">{item.tranx_type}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">{item.wallet_type}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{item.amount}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{item.total}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">{item.message}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{new Date(item.createdAt).toLocaleString()}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={pagination.currentPage}
        totalPages={pagination.totalPages}
        totalRows={pagination.totalRows}
        pageSize={filters.pageSize}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default WalletStatementReport;
